type RGB = `rgb(${number}, ${number}, ${number})`;
type RGBA = `rgba(${number}, ${number}, ${number}, ${number})`;
type HEX = `#${string}`;

export type ColorVector = [number, number, number, number] | [number, number, number];
export type ColorString = RGB | RGBA | HEX;
export type Color = ColorVector | ColorString;

/**
 * Parses color string
 * @param colorString
 * @returns [0..1, 0..1, 0..1, 0..1]
 */
export function parseColor(colorString: string): [number, number, number, number] {
    const [r, g, b, a] = parseColor255(colorString);
    return [r / 255, g / 255, b / 255, a / 255];
}

/**
 * Parses color string
 * @param colorString
 * @returns [0..255, 0..255, 0..255, 0..255]
 */
export function parseColor255(colorString: string): [number, number, number, number] {
    const color = colorString.trim();

    if (color.startsWith('#')) {
        const hex = color.slice(1);

        if (hex.length === 3) {
            // #rgb -> #rrggbb
            const r = parseInt(hex[0] + hex[0], 16);
            const g = parseInt(hex[1] + hex[1], 16);
            const b = parseInt(hex[2] + hex[2], 16);
            return [r, g, b, 255];
        } else if (hex.length === 4) {
            // #rgba -> #rrggbbaa
            const r = parseInt(hex[0] + hex[0], 16);
            const g = parseInt(hex[1] + hex[1], 16);
            const b = parseInt(hex[2] + hex[2], 16);
            const a = parseInt(hex[3] + hex[3], 16);
            return [r, g, b, a];
        } else if (hex.length === 6) {
            // #rrggbb
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return [r, g, b, 255];
        } else if (hex.length === 8) {
            // #rrggbbaa
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            const a = parseInt(hex.slice(6, 8), 16);
            return [r, g, b, a];
        }
    }

    // Parse rgba(r,g,b,a) format
    const rgbaMatch = color.match(/rgba?\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*(?:,\s*(\d+(?:\.\d+)?))?\s*\)/);
    if (rgbaMatch) {
        const r = parseInt(rgbaMatch[1]);
        const g = parseInt(rgbaMatch[2]);
        const b = parseInt(rgbaMatch[3]);
        const a = rgbaMatch[4] ? parseFloat(rgbaMatch[4]) * 255 : 255;
        return [r, g, b, a];
    }

    // Default to black if parsing fails
    console.warn(`Failed to parse color: ${colorString}`);
    return [0, 0, 0, 255];
}

export function toColorString(color: Color): ColorString {
    if (Array.isArray(color)) {
        return (color.length === 4 ? `rgba(${color.join()})` : `rgb(${color.join()})`) as ColorString;
    }
    return color;
}

const altColorTable: ColorVector[] = [
    [0, 0, 255, 255], // 0
    [0, 28, 255],
    [0, 57, 255],
    [0, 85, 255],
    [0, 113, 255],
    [0, 142, 255],
    [0, 170, 255],
    [0, 198, 255],
    [0, 227, 255],
    [0, 255, 255], // 9
    [0, 255, 227],
    [0, 255, 198],
    [0, 255, 170],
    [0, 255, 142],
    [0, 255, 113],
    [0, 255, 85],
    [0, 255, 57],
    [0, 255, 28],
    [0, 255, 0], // 18
    [28, 255, 0],
    [57, 255, 0],
    [85, 255, 0],
    [113, 255, 0],
    [142, 255, 0],
    [170, 255, 0],
    [198, 255, 0],
    [227, 255, 0],
    [255, 255, 0], // 27
    [255, 227, 28],
    [255, 198, 57],
    [255, 170, 85],
    [255, 142, 113],
    [255, 113, 142],
    [255, 85, 170],
    [255, 57, 198],
    [255, 28, 227],
    [255, 0, 255], // 36
    [255, 0, 227],
    [255, 0, 198],
    [255, 0, 170],
    [255, 0, 142],
    [255, 0, 113],
    [255, 0, 85],
    [255, 0, 57],
    [255, 0, 28],
    [255, 0, 0]
];

export function getAltitudeColor(altitude100ft: number): ColorString {
    const ndx = Math.min(altColorTable.length - 1, Math.max(Math.round(altitude100ft / 10), 0));
    return toColorString(altColorTable[ndx]);
}

export function getAltitudeColorVector(altitude100ft: number): [number, number, number, number] {
    const ndx = Math.min(altColorTable.length - 1, Math.max(Math.round(altitude100ft / 10), 0));
    return [...altColorTable[ndx], 255] as [number, number, number, number];
}
